import { coverSrc, servicePrice, withSectionHeaders, type PublicShop, type PublicShopItem } from "@fusiqo/vozqi-shared";
import { RowChevron } from "./row-chevron";
import type { ServicesState } from "./use-services";

/**
 * «Servicios» (spec 2026-10-20 §5): what the business does, each a card with its
 * photo -- through the same door as a product's, vouched for by the page that
 * listed it --, two lines of what it is and the least it costs, headed by the
 * sections that hold services. A card opens the service's own page, saving
 * where the visitor was. None to show -- the last one unshared since the
 * profile was read -- and the part is not drawn: no empty box under its title.
 */
export function ServicesList({
  slug,
  services,
  sections,
  country,
  onOpen,
}: {
  slug: string;
  services: ServicesState;
  sections: PublicShop["sections"];
  country: PublicShop["business"]["country"];
  onOpen: () => void;
}) {
  if (services.status === "idle" && services.items.length === 0) return null;
  const rows = withSectionHeaders(services.items, sections);
  return (
    <section className="profile-section" aria-labelledby="profile-services">
      <h2 id="profile-services" className="profile-section-title">
        Servicios
      </h2>
      <div className="profile-services">
        {rows.map((row) =>
          row.kind === "section" ? (
            <h3 key={row.key} className="profile-services-section">
              {row.name}
            </h3>
          ) : (
            <ServiceCard
              key={row.key}
              item={row.item}
              cover={coverSrc(slug, row.item, services.pageOf[row.item.token] ?? "")}
              price={servicePrice(row.item, country)}
              onOpen={onOpen}
            />
          ),
        )}
        {services.status === "loading" && services.items.length === 0
          ? Array.from({ length: 3 }, (_, index) => (
              <div key={`s-${String(index)}`} className="profile-service profile-service--skeleton share-skeleton" />
            ))
          : null}
      </div>
      {services.more ? (
        <button type="button" className="profile-more" onClick={services.loadMore}>
          Ver más servicios
        </button>
      ) : null}
      {services.status === "failed" ? (
        <p className="shop-empty">
          No pudimos cargar esto.{" "}
          <button type="button" className="share-retry" onClick={services.retry}>
            Reintentar
          </button>
        </p>
      ) : null}
    </section>
  );
}

/** One service's card: here, and under «Más servicios» on an item's page. `onOpen` saves where the visitor was. */
export function ServiceCard({
  item,
  cover,
  price,
  onOpen,
}: {
  item: PublicShopItem;
  cover: string | null;
  price: string | null;
  onOpen?: () => void;
}) {
  return (
    <a className="profile-service" href={`/p/${item.token}`} onClick={onOpen}>
      {cover === null ? (
        <span className="profile-service-photo profile-service-photo--initial" aria-hidden="true">
          {item.name.trim().charAt(0).toUpperCase()}
        </span>
      ) : (
        <img className="profile-service-photo" src={cover} alt="" loading="lazy" decoding="async" />
      )}
      <span className="profile-service-text">
        <span className="profile-service-name">{item.name}</span>
        {item.description === null ? null : <span className="profile-service-description">{item.description}</span>}
        {price === null ? null : <span className="profile-service-price tabular">{price}</span>}
      </span>
      <RowChevron className="profile-service-chevron" />
    </a>
  );
}
